import { Box, Button, Skeleton, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { menLinksSmall, womenLinksSmall } from "../../constants/avatarLinks";
import { CitySimple, CONFIRMATION_DELAY } from "../../constants/const";
import { Messages } from "../../constants/messages";
import { dispatchTxAction } from "../../helper/dispatchTxAction";
import { goToMapSlotPage } from "../../helper/map";

import useCurrentTime from "../../hook/useCurrentTime";
import useGameBankBalance from "../../hook/useGameBankBalance";
import useWallet from "../../hook/useWallet";

import config from "../../config/config";
import { getGameBankAllowance } from "../../helper/contractFunctions/MafiaGameBank";
import {
  approveGameCashAction,
  buyBulletsAction,
  claimBulletFactoryProfitAction,
  getBulletCityMarketInfoAction,
  getNextBulletBuyTimeAction,
  reproduceBulletsAction,
} from "../../reducers/game.slice";
import { getBulletBalance } from "../../reducers/profile.slice";
import {
  convertDateTimeSimple,
  convertDateTimeWithMinutes,
  toastSuccess,
  toUSDFormat,
} from "../../utils/utils";

import useStyles from "./index.styles";

const updateInterval = 60 * 120;

const BulletFactoryPanels = ({
  setOpenBalancePopup,
}: {
  setOpenBalancePopup: (value: boolean) => void;
}) => {
  const { classes } = useStyles();
  const { account } = useWallet();
  const currentTime = useCurrentTime();
  const dispatch = useAppDispatch();

  const {
    businessProfile,
    bulletMarketInfo,
    isLoadingBulletMarketInfo,
    nextBulletBuyTime,
  } = useAppSelector((state) => state.game);

  const { myProfile } = useAppSelector((state) => state.profile);
  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { updateBalance } = useGameBankBalance(account, signMsg, signature);

  const isMyBusiness =
    account?.toLowerCase() === businessProfile?.address?.toLowerCase();

  const remainingTime =
    bulletMarketInfo.lastUpdateTime + updateInterval - currentTime;

  const [amountToBuy, setAmountToBuy] = useState(0);

  const handleReproduceBullet = () => {
    if (!account) return;

    dispatchTxAction(
      dispatch,
      reproduceBulletsAction({ account, cityId: myProfile.city || 0 }),
      () => {
        toastSuccess(Messages.BULLET_FACTORY.REPRODUCE_BULLET.SUCCESS);
        dispatch(
          getBulletCityMarketInfoAction({ cityId: myProfile.city ?? 0 })
        );
      },
      CONFIRMATION_DELAY
    );
  };

  const handleClaimProfit = () => {
    if (!account) return;
    dispatchTxAction(
      dispatch,
      claimBulletFactoryProfitAction({ account, cityId: myProfile.city || 0 }),
      () => {
        toastSuccess(Messages.BULLET_FACTORY.CLAIM_PROFIT.SUCCESS);
        dispatch(
          getBulletCityMarketInfoAction({ cityId: myProfile.city ?? 0 })
        );
      },
      CONFIRMATION_DELAY
    );
  };

  const handleBuyBullets = async () => {
    if (!account) return;
    const buyBullets = () => {
      dispatchTxAction(
        dispatch,
        buyBulletsAction({
          account,
          bulletAmount: amountToBuy,
          message: signMsg,
          signature: signature,
        }),
        () => {
          updateBalance(account, signMsg, signature);
          dispatch(getBulletBalance({ account, signMsg, signature }));
          dispatch(getNextBulletBuyTimeAction(account));
          dispatch(
            getBulletCityMarketInfoAction({ cityId: myProfile.city ?? 0 })
          );
        },
        CONFIRMATION_DELAY,
        undefined
      );
    };

    const allowance = await getGameBankAllowance(
      account,
      config.bulletFactoryAddress
    );

    if (allowance < amountToBuy * bulletMarketInfo.price) {
      dispatchTxAction(
        dispatch,
        approveGameCashAction({
          account,
          cashAmount: 10 ** 18,
          spender: config.bulletFactoryAddress,
        }),
        () => {
          buyBullets();
        },
        CONFIRMATION_DELAY
      );
    } else {
      buyBullets();
    }
  };

  useEffect(() => {
    dispatch(getBulletCityMarketInfoAction({ cityId: myProfile.city ?? 0 }));
  }, [dispatch, myProfile.city]);

  useEffect(() => {
    if (remainingTime === 0) {
      dispatch(getBulletCityMarketInfoAction({ cityId: myProfile.city ?? 0 }));
    }
  }, [bulletMarketInfo.price, dispatch, myProfile.city, remainingTime]);

  return (
    <Box className={classes.body}>
      <Box className={classes.purchaseBox}>
        <Box className={classes.info}>
          <Typography className={classes.infoTitle}>Price</Typography>
          <Box className={classes.infoBox}>
            {isLoadingBulletMarketInfo ? (
              <Skeleton width={80} height={32} />
            ) : (
              <Typography className={classes.priceInfoValue}>
                ${toUSDFormat(bulletMarketInfo.price)}
              </Typography>
            )}
            <Typography className={classes.priceInfoLabel}>
              Per bullet
            </Typography>
          </Box>
        </Box>

        <Box className={classes.info}>
          <Typography className={classes.infoTitle}>
            Available bullets
          </Typography>
          <Box className={classes.infoBox}>
            <Box className={classes.infoIconBox}>
              <Box
                component={"img"}
                src="/assets/imgs/inventory/bulletitem.png"
              ></Box>
              {isLoadingBulletMarketInfo ? (
                <Skeleton width={80} height={32} />
              ) : (
                <Typography className={classes.priceInfoValue}>
                  {toUSDFormat(bulletMarketInfo.amountLeft)}
                </Typography>
              )}
            </Box>
            <Typography className={classes.priceInfoLabel}>
              Total available
            </Typography>
          </Box>
        </Box>

        <Box className={classes.info}>
          <Typography className={classes.infoTitle}>Amount to buy</Typography>
          <Box className={classes.infoBox}>
            <Box className={classes.priceInputBox}>
              {/* Input box  */}
              <input
                type="number"
                className={classes.priceInput}
                placeholder="0"
                min={0}
                max={bulletMarketInfo.amountLeft}
                step={1}
                value={amountToBuy}
                onChange={(e) => {
                  setAmountToBuy(Number(e.target.value) || 0);
                }}
              />

              <Button
                className={classes.priceAllButton}
                onClick={() => {
                  setAmountToBuy(bulletMarketInfo.amountLeft);
                }}
              >
                ALL
              </Button>
            </Box>
            <Typography className={classes.priceInfoValue}></Typography>
            <Typography className={classes.priceInfoLabel}>
              Define amount
            </Typography>
          </Box>
        </Box>

        <Box className={classes.info}>
          <Box className={classes.infoIconBox}>
            <Box component={"img"} src="/assets/imgs/layout/currency.png"></Box>
            <Typography className={classes.priceInfoValue}>
              ${toUSDFormat(amountToBuy * bulletMarketInfo.price)}
            </Typography>
          </Box>

          <Button
            className={classes.purchaseButton}
            onClick={handleBuyBullets}
            disabled={amountToBuy <= 0 || nextBulletBuyTime > currentTime}
          >
            Confirm purchase
          </Button>

          {nextBulletBuyTime > currentTime ? (
            <Typography className={classes.purchaseButtonLabel}>
              You need to wait{" "}
              {convertDateTimeWithMinutes(nextBulletBuyTime - currentTime)} to
              buy
            </Typography>
          ) : (
            <Typography className={classes.purchaseButtonLabel}>
              You can buy bullets now
            </Typography>
          )}
        </Box>
      </Box>

      <Box className={classes.container}>
        <Box className={classes.timerBox}>
          <Box component="img" src="/assets/imgs/travel/clockwise.png" />
          <Typography className={classes.remainTime}>
            {remainingTime > 0
              ? `in ${convertDateTimeWithMinutes(remainingTime)}`
              : "Now"}
          </Typography>

          {remainingTime > 0 ? (
            <Typography className={classes.infoText}>
              New bullets produced
            </Typography>
          ) : (
            <Button
              className={classes.reproduceBulletButton}
              onClick={handleReproduceBullet}
            >
              Reproduce bullets
            </Button>
          )}
        </Box>

        {/* Bullet Factory panel */}
        <Box className={classes.panel}>
          <Typography className={classes.title}>Bullet Factory</Typography>
          <Box className={classes.details}>
            {businessProfile?.ownerImageId ? (
              <Box
                component="img"
                className={classes.image}
                src={
                  businessProfile?.ownerGender === 1
                    ? womenLinksSmall[
                        !businessProfile.ownerImageId
                          ? -1
                          : businessProfile.ownerImageId
                      ]
                    : menLinksSmall[
                        !businessProfile?.ownerImageId
                          ? -1
                          : businessProfile.ownerImageId
                      ]
                }
              ></Box>
            ) : (
              <Box sx={{ minWidth: 100 }}></Box>
            )}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: isMyBusiness ? 1 : 2,
              }}
            >
              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>Owner:</Typography>
                <Typography
                  className={`${classes.infoValue} ${classes.ownerLink}`}
                >
                  {businessProfile?.ownerName}
                </Typography>
              </Box>

              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>Family:</Typography>
                <Typography className={classes.infoValue}>
                  {businessProfile?.familyInfo?.name || " "}
                </Typography>
              </Box>

              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>Location:</Typography>
                <Typography
                  className={`${classes.infoValue} ${classes.ownerLink}`}
                  sx={{ cursor: "pointer", color: "#1c7995" }}
                  onClick={() =>
                    businessProfile &&
                    businessProfile.map &&
                    goToMapSlotPage(
                      businessProfile.cityId,
                      businessProfile.map?.slotX,
                      businessProfile.map?.slotY
                    )
                  }
                >
                  {businessProfile &&
                    businessProfile?.cityId >= 0 &&
                    `#${CitySimple[businessProfile.cityId]}-${
                      businessProfile.map?.slotX
                    }-${businessProfile.map?.slotY}`}
                </Typography>
              </Box>

              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>
                  Time owned:
                </Typography>
                <Typography className={classes.infoValue}>
                  {convertDateTimeSimple(
                    currentTime -
                      (businessProfile?.timestamp || currentTime - 10000000)
                  )}
                </Typography>
              </Box>

              {isMyBusiness && (
                <Button
                  className={classes.claimButton}
                  onClick={handleClaimProfit}
                >
                  Claim profit
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BulletFactoryPanels;
